import React, { FC, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { UseFormReturn } from "react-hook-form";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { StakeholderDto } from "@green0meter/api-client";

import { StakeholderFormValues } from "../../StakeholdersEditor";

import { Stack, Button, Typography } from "@components/base";
import {
  useCreateStakeholder,
  useUpdateStakeholder,
  useDeleteStakeholder,
} from "@api";
import { useUserStore } from "@store/user";
import { getYearFromSearchParams } from "@helpers";

interface StakeholderEditorActionsProps {
  handleClose: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: UseFormReturn<StakeholderFormValues, any, any>;
  stakeholder: StakeholderDto | null;
  onDeleteSuccess?: () => void;
  onUpdateSuccess?: () => void;
  onCreateSuccess?: () => void;
  onPendingChange?: (isPending: boolean) => void;
}

const StakeholderEditorActions: FC<StakeholderEditorActionsProps> = ({
  handleClose,
  form,
  stakeholder,
  onDeleteSuccess,
  onUpdateSuccess,
  onCreateSuccess,
  onPendingChange,
}) => {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const [searchParams] = useSearchParams();
  const year = getYearFromSearchParams(searchParams);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const isCreateMode = !stakeholder;

  const {
    createStakeholderAsync,
    isPendingCreateStakeholder,
    isSuccessCreateStakeholder,
  } = useCreateStakeholder(user);
  const {
    updateStakeholderAsync,
    isPendingUpdateStakeholder,
    isSuccessUpdateStakeholder,
  } = useUpdateStakeholder(user);
  const {
    deleteStakeholderAsync,
    isPendingDeleteStakeholder,
    isSuccessDeleteStakeholder,
  } = useDeleteStakeholder(user);

  const {
    handleSubmit,
    formState: { isValid, isDirty },
  } = form;

  useEffect(() => {
    if (onPendingChange) {
      onPendingChange(
        isPendingCreateStakeholder ||
        isPendingUpdateStakeholder ||
        isPendingDeleteStakeholder,
      );
    }
  }, [
    isPendingCreateStakeholder,
    isPendingUpdateStakeholder,
    isPendingDeleteStakeholder,
    onPendingChange,
  ]);

  useEffect(() => {
    if (isSuccessCreateStakeholder) {
      if (onCreateSuccess) {
        onCreateSuccess();
      }
    }
  }, [isSuccessCreateStakeholder, onCreateSuccess]);

  useEffect(() => {
    if (isSuccessUpdateStakeholder) {
      if (onUpdateSuccess) {
        onUpdateSuccess();
      }
    }
  }, [isSuccessUpdateStakeholder, onUpdateSuccess]);

  useEffect(() => {
    if (isSuccessDeleteStakeholder) {
      setShowDeleteDialog(false);
      if (onDeleteSuccess) {
        onDeleteSuccess();
      }
    }
  }, [isSuccessDeleteStakeholder, onDeleteSuccess]);

  const onSubmit = async (data: StakeholderFormValues): Promise<void> => {
    if (isCreateMode) {
      await createStakeholderAsync({
        fullName: data.fullName,
        email: data.email,
        companyName: data.companyName,
        comment: data.comment || "",
        emailReminders: data.emailReminders,
        language: data.language,
        type: data.type,
        impactAreas: data.impactAreas,
        esrsAreas: data.esrsAreas,
        deadline: data.deadline,
        year,
      });
    } else {
      await updateStakeholderAsync({
        id: stakeholder.id,
        fullName: data.fullName,
        email: data.email,
        companyName: data.companyName,
        comment: data.comment || "",
        emailReminders: data.emailReminders,
        language: data.language,
        type: data.type,
        impactAreas: data.impactAreas,
        esrsAreas: data.esrsAreas,
        deadline: data.deadline,
      });
    }
  };

  const handleDeleteClick = (): void => {
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    if (!stakeholder) {
      throw new Error("Stakeholder not found");
    }

    await deleteStakeholderAsync(stakeholder.id);
  };

  const handleDeleteCancel = (): void => {
    setShowDeleteDialog(false);
  };

  const deleteDialog = (
    <Dialog open={showDeleteDialog} onClose={handleDeleteCancel}>
      <DialogTitle>{"Confirm Deletion"}</DialogTitle>
      <DialogContent>
        <Typography>
          {
            "Are you sure you want to remove this stakeholder? This action cannot be undone."
          }
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDeleteCancel} disabled={isPendingDeleteStakeholder}>
          {t("common.cancel")}
        </Button>
        <Button
          onClick={handleDeleteConfirm}
          color="error"
          variant="contained"
          disabled={isPendingDeleteStakeholder}
          isLoading={isPendingDeleteStakeholder}
        >
          {"Remove"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const isPending =
    isPendingCreateStakeholder ||
    isPendingUpdateStakeholder ||
    isPendingDeleteStakeholder;

  return (
    <>
      <Stack direction="row" spacing={2} justifyContent="space-between">
        {!isCreateMode && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteClick}
            disabled={isPending}
          >
            {t("common.remove")}
          </Button>
        )}
        <Stack
          direction="row"
          spacing={2}
          sx={{ marginLeft: isCreateMode ? "auto" : undefined }}
        >
          <Button variant="outlined" onClick={handleClose} disabled={isPending}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit(onSubmit)}
            disabled={!isValid || !isDirty || isPending}
            isLoading={isPendingCreateStakeholder || isPendingUpdateStakeholder}
          >
            {isCreateMode ? t("common.create") : t("common.save")}
          </Button>
        </Stack>
      </Stack>
      {!isCreateMode && deleteDialog}
    </>
  );
};

export default StakeholderEditorActions;
